import React from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material'
import { H6 } from '../../../../../../layouts/TextStyles'
import CustomInput from '../../../../../../components/muiComponents/customInput/CustomInput'
import { Lorum } from '../../../../../../utils/constants'
import { CoursePackageState } from '../../../../../../features/types'
import { isNumber, requiredString } from '../../../../../../utils/validation'
import { coursePackageApi } from '../../../../../../features/course/coursePackage'
import { tone } from '../../../../../../utils/uiTokens'

interface Props {
    editPackage?: CoursePackageState
    coursePriceId: number
    open: boolean
    handleClose: () => void
}

const styles = {
    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
    fullWidth: {
        width: '100%',
    },
    flex: {
        display: 'flex',
    },
}

const BundleDialog: React.FC<Props> = ({
    editPackage = null,
    coursePriceId,
    open,
    handleClose,
}) => {
    const { formatMessage } = useIntl()

    const [createPackage, { isLoading: isCreating }] =
        coursePackageApi.useCreateCoursePackageMutation()
    const [updatePackage, { isLoading: isUpdating }] =
        coursePackageApi.useUpdateCoursePackageMutation()

    const isLoading = isCreating || isUpdating
    const coursePackage: CoursePackageState = {
        id: editPackage?.id ?? null,
        sloganKz: editPackage?.sloganKz ?? '',
        sloganRu: editPackage?.sloganRu ?? '',
        coursePriceId: coursePriceId,
        discount: editPackage?.discount ?? null,
        subjectCount: editPackage?.subjectCount ?? null,
        price: editPackage?.price ?? '',
        yearlyPrice: editPackage?.yearlyPrice ?? ''
    }

    const validationSchema = yup.object().shape({
        sloganKz: requiredString({ formatMessage }),
        sloganRu: requiredString({ formatMessage }),
        discount: isNumber({ formatMessage }),
        subjectCount: isNumber({ formatMessage }),
    })

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: coursePackage,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            if (editPackage) {
                handleUpdatePackage()
            } else {
                handleCreatePackage()
            }
        },
    })

    const handleCreatePackage = async () => {
        try {
            await createPackage(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            handleClose()
        } catch (error) {}
    }

    const handleUpdatePackage = async () => {
        try {
            await updatePackage(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    return (
        <Dialog
            maxWidth={'md'}
            fullWidth
            open={open}
            onClose={handleClose}
        >
            <DialogTitle>Пакет</DialogTitle>

            <DialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Box sx={styles.flex}>
                        <Box
                            sx={styles.fullWidth}
                            mr={4}
                        >
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'subject' })}</H6>

                                <CustomInput
                                    id="subjectCount"
                                    name="subjectCount"
                                    value={formik.values.subjectCount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.subjectCount)}
                                    helperText={formik.errors.subjectCount}
                                    placeholder={formatMessage({
                                        id: 'subject',
                                    })}
                                    fullWidth
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'sloganKz' })}</H6>

                                <CustomInput
                                    id="sloganKz"
                                    name="sloganKz"
                                    value={formik.values.sloganKz}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.sloganKz)}
                                    helperText={formik.errors.sloganKz}
                                    placeholder={Lorum}
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    minRows={5}
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'price' })}</H6>

                                <CustomInput
                                    id="price"
                                    name="price"
                                    value={formik.values.price}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.price)}
                                    placeholder={formatMessage({
                                        id: 'price',
                                    })}
                                    fullWidth
                                />
                            </Box>
                        </Box>

                        <Box sx={styles.fullWidth}>
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'discount%' })}</H6>

                                <CustomInput
                                    id="discount"
                                    name="discount"
                                    value={formik.values.discount}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.discount)}
                                    helperText={formik.errors.discount}
                                    placeholder={formatMessage({
                                        id: 'percent',
                                    })}
                                    fullWidth
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'sloganKz' })}</H6>

                                <CustomInput
                                    id="sloganRu"
                                    name="sloganRu"
                                    value={formik.values.sloganRu}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.sloganRu)}
                                    helperText={formik.errors.sloganRu}
                                    placeholder={Lorum}
                                    fullWidth
                                    multiline
                                    maxRows={5}
                                    minRows={5}
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'yearlyPrice' })}</H6>

                                <CustomInput
                                    id="yearlyPrice"
                                    name="yearlyPrice"
                                    value={formik.values.yearlyPrice}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={Boolean(formik.errors.yearlyPrice)}
                                    placeholder={formatMessage({
                                        id: 'yearlyPrice',
                                    })}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>

                    <DialogActions>
                        <Button
                            onClick={() => formik.handleSubmit()}
                            variant="contained"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress
                                    size={20}
                                    style={{ marginRight: '8px' }}
                                />
                            ) : editPackage ? (
                                formatMessage({ id: 'update' })
                            ) : (
                                formatMessage({ id: 'add' })
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default BundleDialog
